import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { type UniqueIdentifier, useDroppable } from '@dnd-kit/core';

import { TagChip } from '@/components/tag-chip';
import { SortableList } from '@/components/sortable-list';
import { Popover } from '@flintmint/web-shared/components/popover';

import type { Tag } from '@flintmint/core-shared/rxdb';

import styles from './overflowing-tags.module.scss';

export const OverflowingTags = (props: OverflowingTagsProps) => {
  const { tags, isDragging, activeId, overId: _overId, onDelete, overflowCount, appearance = 'dark', ...rest } = props;

  const [open, setOpen] = useState(false);
  const [openedManually, setOpenedManually] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: 'overflow-tags',
  });

  useEffect(() => {
    if (isDragging && isOver) {
      setOpen(true);
    }
  }, [isDragging, isOver]);

  return (
    <Popover
      arrow
      subtleShadow
      offset={[0, 10]}
      cornerRadius="medium"
      open={openedManually || open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setOpenedManually(false);
      }}
      {...rest}
      content={
        <>
          <SortableList
            id="overflow"
            items={tags}
            activeId={activeId}
            className={styles.overflowingTags}
            renderItem={(props) => {
              const { value, index: _index, dragging, listeners: _listeners, dragOverlay, ...rest } = props;
              return (
                <TagChip draggable tag={value!} dragging={dragging} dragOverlay={dragOverlay} onDelete={onDelete} {...rest} />
              );
            }}
          />
        </>
      }
    >
      <div className={styles.overflowCountHolder}>
        <span
          ref={setNodeRef}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenedManually(true);
          }}
          className={clsx(styles.overflowCount, styles[`a-${appearance}`], 'hidden-tags-number', {
            [styles.dragOver]: isDragging && isOver,
          })}
          data-testid="hidden-tags-plus"
        >
          +{overflowCount}
        </span>
      </div>
    </Popover>
  );
};

export interface OverflowingTagsProps {
  tags: Tag[];
  isDragging?: boolean;
  overflowCount: number;
  onDelete?: (tag: Tag) => void;
  overId?: UniqueIdentifier | null;
  activeId?: UniqueIdentifier | null;
  appearance?: 'greyscale' | 'dark';
}
